import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { Row, Col, Card, Table, Pagination, Button, Form, Spinner } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { getMonthlyRevenueReportList } from "../../redux/actions/VerifiedReportActions";
import { getMasterDriverList, getMasterVehicleList, getMasterVendorList, getAllUsersRoleWiseList } from "../../redux/actions/MasterActions";
import RoundedPagination from "../../Components/Paginate";
import FormInput from "../../Components/FormInput";
import { Link, useNavigate } from "react-router-dom";
import swal from 'sweetalert2';
import { toast } from "react-toastify";
import { logout } from "../../redux/actions/AuthActions";
import useDebounce from "../../hooks/useDebounce";
import { getDisplayDateTimeFormat } from '../../util/generalUtills'
import { CSVLink } from 'react-csv';
import DatePicker from "react-datepicker";
import { addDays } from "date-fns";
import Select from "react-dropdown-select";
import Preloader from "../../Components/Preloader";
import ExportToExcel from "../../Components/ExportToExcel";
import PrintTable from "../../Components/PrintTable";


const Dashboard = () => {
  const { t } = useTranslation();
  const tableRef = useRef();
  const [swalProps, setSwalProps] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    AuthReducer: { auth, errors, loading },
    verifiedReportReducer: {
      monthlyRevenueReportLists,
      reportPermissions,
      loadingReport,
      errorsReport,
      reportCount,
    },
    masterReducer: {
      masterVehicleLists,
      masterDriverLists,
      masterVendorLists,
      masterUsersRoleWiseLists,
      loadingMaster,
      errorsMaster,
    },
  } = useSelector((state) => state);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState('desc');
  const indexOfLastItem = (page + 1) * paginate;
  const indexOfFirstItem = indexOfLastItem - paginate;
  const [jenId, setJenId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [validated, setValidated] = useState(false);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    if (auth?.accessToken) {
      dispatch(getAllUsersRoleWiseList(auth?.accessToken, 'JUNIOR_ENGINEER'));
      dispatch(getMasterVendorList(auth?.accessToken, jenId));
    }
  }, [auth?.accessToken, jenId, dispatch]);

  useEffect(() => {
    if (jenId && auth?.accessToken) {
      dispatch(getMasterVehicleList(auth?.accessToken, jenId, vendorId));
      dispatch(getMasterDriverList(auth?.accessToken, jenId, vendorId));
    }
  }, [jenId, vendorId, auth?.accessToken, dispatch])

  const fetchMonthlyRevenueReportList = useCallback(() => {
    if (auth?.accessToken) {
      dispatch(getMonthlyRevenueReportList(auth?.accessToken, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, startDate, endDate));
    }
  }, [auth?.accessToken, paginate, page, search, sort, dispatch]);

  useDebounce(fetchMonthlyRevenueReportList, 500, [auth?.accessToken, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, startDate, endDate]);

  const csvHead = [['SR No.', 'User', 'Tanker Number', 'Total Trip', 'Distance']]
  const csvData = monthlyRevenueReportLists.map((cv, index) => [
    index + 1,
    cv?.jen_idBookingDetailsAdminUsers?.name,
    cv?.vehicle_number,
    cv?.count,
    cv?.distance,
  ])
  const exportData = csvHead.concat(csvData);

  useEffect(() => {
    if (errors) {
      if (errors?.status === 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }

  }, [errors])

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
    dispatch(getMonthlyRevenueReportList(auth?.accessToken, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, startDate, endDate));
  };

  const handlePaginateChange = (e) => {
    const value = e.target.value === "All" ? reportCount : parseInt(e.target.value);
    setPaginate(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const transformedVendorOptions = useMemo(() =>
    masterVendorLists?.map(vendor => ({
      ...vendor,
      vendorId: vendor.vendorId || vendor.id,
      vendorName: vendor.vendorName || vendor.name
    })) || [],
    [masterVendorLists]
  );

  const transformedVehicleOptions = useMemo(() =>
    masterVehicleLists?.map(vehicle => ({
      ...vehicle,
      vehicleId: vehicle.id || vehicle.vehicleId,
      vehicleNumber: vehicle.vehicle_number || vehicle.vehicleNumber
    })) || [],
    [masterVehicleLists]
  );

  const transformedDriverOptions = useMemo(() =>
    masterDriverLists?.map(driver => ({
      ...driver,
      driverId: driver.id || driver.driverId,
      driverName: driver.driver_name || driver.driverName
    })) || [],
    [masterDriverLists]
  );

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      '&:hover': {
        borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      }
    }),
  };

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: t("Monthly Revenue Report"), path: "/Monthly-Revenue-Report", active: true },
        ]}
        title={t("Monthly Revenue Report")}
      />

      <Row>
        <Col>
          {(reportPermissions?.filter) && (
            <>
              <Card>
                <Card.Body>
                  <Row>
                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("J.E.N.")}</Form.Label>
                        <Select
                          options={masterUsersRoleWiseLists || []}
                          values={masterUsersRoleWiseLists?.filter(name => name.id === jenId) || []}
                          name="jen_id"
                          labelField={"name"}
                          valueField={"id"}
                          searchable={true}
                          searchBy="name"
                          dropdownHeight="300px"
                          separator={true}
                          onChange={(selectedData) => {
                            setJenId(selectedData[0]?.id || "");
                            setVendorId(""); // Reset vendor when JEN changes
                            setVehicleId(""); // Reset vehicle when JEN changes
                            setDriverId(""); // Reset driver when JEN changes
                          }}
                          styles={customSelectStyles}
                        />
                        {errorsMaster?.data?.jen_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.jen_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Vendor")}</Form.Label>
                        <Select
                          options={transformedVendorOptions}
                          values={transformedVendorOptions?.filter(name => name.vendorId === vendorId) || []}
                          name="vendor_id"
                          labelField={"vendorName"}
                          valueField={"vendorId"}
                          searchBy={"vendorName"}
                          onChange={(selectedData) => {
                            setVendorId(selectedData[0]?.vendorId || "");
                            setVehicleId(""); // Reset vehicle when vendor changes
                            setDriverId(""); // Reset driver when vendor changes
                          }}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.vendor_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.vendor_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Vehicle")}</Form.Label>
                        <Select
                          options={transformedVehicleOptions}
                          values={transformedVehicleOptions?.filter(option => option.vehicleId === vehicleId) || []}
                          name="vehicle_id"
                          labelField={"vehicleNumber"}
                          valueField={"vehicleId"}
                          searchBy={"vehicleNumber"}
                          onChange={(selectedData) => setVehicleId(selectedData[0]?.vehicleId || "")}
                          disabled={!jenId}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.vehicle_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.vehicle_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Driver")}</Form.Label>
                        <Select
                          options={transformedDriverOptions}
                          values={transformedDriverOptions?.filter(option => option.driverId === driverId) || []}
                          name="driver_id"
                          labelField={"driverName"}
                          valueField={"driverId"}
                          searchBy={"driverName"}
                          onChange={(selectedData) => setDriverId(selectedData[0]?.driverId || "")}
                          disabled={!jenId}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.driver_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.driver_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Date")}</Form.Label>
                        <DatePicker
                          selectsRange={true}
                          dateFormat="yyyy-MM-dd"
                          startDate={startDate}
                          endDate={endDate}
                          todayButton="Today"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                          maxDate={addDays(new Date(), 1)}
                          className="form-control"
                          onChange={(update) => {
                            if (update.length === 2) {
                              const date1 = new Date(update[0]);
                              date1.setHours(12, 0, 0, 0);
                              setDateRange([date1, null]);
                              if (update[1] != null) {
                                const date2 = new Date(update[1] || update[0]);
                                date2.setHours(12, 0, 0, 0);
                                setDateRange([date1, date2]);
                              }
                            }
                          }}
                          withPortal
                        />
                        {errorsMaster?.data?.startDate && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.startDate}</p>
                          </div>
                        )}
                        {errorsMaster?.data?.endDate && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.endDate}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2} >
                      <div className="mb-3" style={{ marginTop: '1.6rem' }}>
                        {loadingReport ?
                          <Button color="primary" disabled>
                            <Spinner
                              className="spinner-border-sm me-1"
                              tag="span"
                              color="white"
                            />
                            Please wait ...
                          </Button>
                          :
                          <Button type="button" onClick={handleSubmit} disabled={loadingReport}>
                            Submit
                          </Button>
                        }
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}

          <Card>
            <Card.Body>
              <div className="mb-2">
                <Row>
                  <Col lg={3}>
                    {(reportPermissions?.filter) && (
                      <>
                        <div className="d-flex align-items-center">
                          <input
                            type="search"
                            placeholder="Search..."
                            className="form-control ms-1"
                            autoComplete="off"
                            value={search}
                            onChange={handleSearchChange}
                          />
                        </div>
                      </>
                    )}
                  </Col>
                  <Col lg={9}>
                    <div className="d-flex justify-content-end align-items-right">
                      <Col lg={3}>
                        <Row>
                          <Col lg={2}></Col>
                          {(reportPermissions?.print) && (
                            <>
                              <Col lg={5}>
                                <PrintTable
                                  tableId="table-to-print"
                                  logoUrl="https://admin.brainbird.co.in/logo.jpg"
                                  title=""
                                  removeLastColumn={false}
                                />
                              </Col>
                            </>
                          )}

                          {(reportPermissions?.excel) && (
                            <>
                              <Col lg={5}>
                                <ExportToExcel tableRef={tableRef} filename="data.xlsx" />
                              </Col>
                            </>
                          )}
                        </Row>
                      </Col>
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={{ overflowY: 'auto', maxHeight: '500px', border: "1px solid #dee2e6" }}>
                <Table ref={tableRef} className="mb-0 table-bordered"
                  size="sm"
                  striped
                  responsive
                  id="table-to-print"
                  style={{ width: "100%", tableLayout: "auto" }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                    <tr>
                      <th>SR No.</th>
                      <th>User</th>
                      <th>Tanker Number</th>
                      <th>Total Trip</th>
                      <th>Distance</th>
                      <th>Water Supply (K.L.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyRevenueReportLists.length === 0 ? (
                      <tr>
                        <td colSpan="5">No data found</td>
                      </tr>
                    ) : (
                      monthlyRevenueReportLists?.map?.((item, index) => (
                        <tr key={item.id}>
                          <th scope="row">{index + 1 + (page - 1) * paginate}</th>
                          <td>{item?.userName}</td>
                          <td>{item?.vehicleNumber}</td>
                          <td>{item?.totalTrip}</td>
                          <td>{item?.totalDistance}</td>
                          <td>{parseFloat(item?.totalWaterSupply).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3 d-lg-flex align-items-center text-center pb-1">
                <div className="d-inline-block me-3">
                  <label className="me-1">Display :</label>
                  <select className="form-select d-inline-block w-auto" onChange={handlePaginateChange}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <span className="me-3">Page <strong>{page}</strong> of <strong>{Math.ceil(reportCount / paginate)}</strong> </span>
                <RoundedPagination
                  currentPage={page}
                  totalPages={Math.ceil(reportCount / paginate)}
                  onPageChange={(pageNumber) => setPage(pageNumber)}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loadingMaster && (<Preloader />)}
      {loadingReport && (<Preloader />)}
    </>
  );
};

export default Dashboard;
